import {RegionCode} from '../../backend/enums';
import {UserFormType} from '../../enums';
import {translations} from '../../i18n/translations';
import {
  EMAIL_REGEX,
  QUICK_ACCESS_CODE_MAX_CHARACTERS,
  QUICK_ACCESS_CODE_REGEX,
  ROOM_NAME_MAX_CHARACTERS,
} from '../../utils/constants';
import {validateMobilePhoneNumber} from '../../utils/functions';

const IDENTIFICATION_GENERIC_NUMBER_REGEX: RegExp = /^[0-9]+$/;
const TAX_IDENTIFICATION_NUMBER_PT_REGEX: RegExp = /^[0-9]{9}$/;
const TAX_IDENTIFICATION_NUMBER_ES_REGEX: RegExp = /^[0-9]{12}$/;
const TAX_IDENTIFICATION_NUMBER_BR_REGEX: RegExp = /^[0-9]{14}$/;

const HEALTH_NUMBER_PT_REGEX: RegExp = /^[0-9]{9}$/; // SNS number
const HEALTH_NUMBER_ES_REGEX: RegExp = /^[0-9]{10,16}$/; // SNS number
const HEALTH_NUMBER_BR_REGEX: RegExp = /^[0-9]{15}$/; // CNS number

const SOCIAL_SECURITY_NUMBER_PT_REGEX: RegExp = /^[0-9]{11}$/; // NISS number
const SOCIAL_SECURITY_NUMBER_ES_REGEX: RegExp = /^[0-9]{12}$/; // NUSS number
const SOCIAL_SECURITY_NUMBER_BR_REGEX: RegExp = /^[0-9]{10}$/; // INSS number

const NATIONAL_ID_NUMBER_PT_REGEX: RegExp = /^[0-9A-Z\s]{7,14}$/; // CC number
const NATIONAL_ID_NUMBER_ES_REGEX: RegExp = /^[0-9a-zA-Z]{9}$/; // DNI number
const NATIONAL_ID_NUMBER_BR_REGEX: RegExp = /^[0-9]{11}$/; // CPF number

// room name regex that only allows: alphanumeric, dot, comma, and hyphen
const ROOM_NAME_REGEX: RegExp = /^[a-zA-Z0-9.,-]+$/;

type NumberValidationType = {
  valid: boolean;
  error?: string;
  digits?: string;
  letters?: string;
  region?: RegionCode;
};

const isNameLengthValid = (maximum: number, name: string): boolean => {
  return name.trim().length <= maximum;
};

export const nameValidation = (
  maximum: number,
  name?: string
): {valid: boolean; error?: string; max?: number} => {
  if (name && name.trim().length > 0) {
    const nameValid = isNameLengthValid(maximum, name);

    if (!nameValid) {
      return {
        valid: false,
        max: maximum,
        error:
          translations.user_form.errors.form_validation_invalid_display_name_maximum_characters,
      };
    } else {
      return {
        valid: true,
      };
    }
  }

  return {
    valid: false,
    error: translations.user_form.errors.edit_form_validation_invalid_field,
  };
};

export const phoneNumberValidation = (
  countryCodePhone: string,
  userFormType?: UserFormType,
  phoneNumber?: string
): {valid: boolean; error?: string} => {
  if (phoneNumber && phoneNumber.trim().length > 0) {
    const phoneNumberValidation = validateMobilePhoneNumber(
      `${countryCodePhone}${phoneNumber?.trim()}`
    );

    if (
      phoneNumberValidation.isMobilePhoneNumber === true &&
      phoneNumberValidation.isValidPhoneNumber === true
    ) {
      return {valid: true};
    } else if (
      phoneNumberValidation.isMobilePhoneNumber === false &&
      phoneNumberValidation.isValidPhoneNumber === true
    ) {
      if (
        userFormType === UserFormType.EditCurrentUser ||
        userFormType === UserFormType.EditCaregiver_SystemAdministrator ||
        !userFormType
      ) {
        return {
          valid: false,
          error: translations.user_form.errors.form_validation_not_mobile_phone_number,
        };
      } else if (
        userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
        userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator
      ) {
        return {
          valid: true,
          error: undefined,
        };
      }
    } else {
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_phone_number,
      };
    }
  }

  if (
    userFormType === UserFormType.EditCurrentUser ||
    userFormType === UserFormType.EditCaregiver_SystemAdministrator
  ) {
    if (userFormType === UserFormType.EditCurrentUser) {
      return {
        valid: false,
        error: translations.user_form.errors.edit_form_validation_invalid_field,
      };
    } else if (userFormType === UserFormType.EditCaregiver_SystemAdministrator) {
      return {
        valid: false,
        error: translations.user_form.errors.edit_form_validation_invalid_field,
      };
    }
  } else if (
    userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
    userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator
  ) {
    return {
      valid: true,
    };
  }

  return {valid: true};
};

export const emailValidation = (email?: string): {valid: boolean; error?: string} => {
  if (email && email.trim().length > 0) {
    if (EMAIL_REGEX.test(email)) {
      return {valid: true};
    } else {
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_email,
      };
    }
  }
  return {valid: true};
};

export const taxPayerNumberValidation = (
  number?: string,
  region?: RegionCode
): NumberValidationType => {
  if (number && number.trim().length > 0) {
    if (region === RegionCode.PT || region === RegionCode.BR || region === RegionCode.ES) {
      if (
        (region === RegionCode.PT && TAX_IDENTIFICATION_NUMBER_PT_REGEX.test(number)) ||
        (region === RegionCode.BR && TAX_IDENTIFICATION_NUMBER_BR_REGEX.test(number)) ||
        (region === RegionCode.ES && TAX_IDENTIFICATION_NUMBER_ES_REGEX.test(number))
      ) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: region === RegionCode.PT ? '9' : region === RegionCode.BR ? '14' : '12',
        region: region,
      };
    } else {
      if (IDENTIFICATION_GENERIC_NUMBER_REGEX.test(number)) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: '',
      };
    }
  }

  return {valid: true};
};

export const nationalHealthServiceNumberValidation = (
  number?: string,
  region?: RegionCode
): NumberValidationType => {
  if (number && number.trim().length > 0) {
    if (region === RegionCode.PT || region === RegionCode.BR || region === RegionCode.ES) {
      if (
        (region === RegionCode.PT && HEALTH_NUMBER_PT_REGEX.test(number)) ||
        (region === RegionCode.BR && HEALTH_NUMBER_BR_REGEX.test(number)) ||
        (region === RegionCode.ES && HEALTH_NUMBER_ES_REGEX.test(number))
      ) {
        return {valid: true};
      }

      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: region === RegionCode.PT ? '9' : region === RegionCode.BR ? '15' : '10-16',
        region: region,
      };
    } else {
      if (IDENTIFICATION_GENERIC_NUMBER_REGEX.test(number)) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: '',
      };
    }
  }

  return {valid: true};
};

export const socialSecurityNumberValidation = (
  number?: string,
  region?: RegionCode
): NumberValidationType => {
  if (number && number.trim().length > 0) {
    if (region === RegionCode.PT || region === RegionCode.BR || region === RegionCode.ES) {
      if (
        (region === RegionCode.PT && SOCIAL_SECURITY_NUMBER_PT_REGEX.test(number)) ||
        (region === RegionCode.BR && SOCIAL_SECURITY_NUMBER_BR_REGEX.test(number)) ||
        (region === RegionCode.ES && SOCIAL_SECURITY_NUMBER_ES_REGEX.test(number))
      ) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: region === RegionCode.PT ? '11' : region === RegionCode.BR ? '10' : '12',
        region: region,
      };
    } else {
      if (IDENTIFICATION_GENERIC_NUMBER_REGEX.test(number)) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: '',
      };
    }
  }

  return {valid: true};
};

export const nationalIdNumberValidation = (
  number?: string,
  region?: RegionCode
): NumberValidationType => {
  if (number && number.trim().length > 0) {
    if (region === RegionCode.PT || region === RegionCode.BR || region === RegionCode.ES) {
      if (
        (region === RegionCode.PT && NATIONAL_ID_NUMBER_PT_REGEX.test(number)) ||
        (region === RegionCode.BR && NATIONAL_ID_NUMBER_BR_REGEX.test(number)) ||
        (region === RegionCode.ES && NATIONAL_ID_NUMBER_ES_REGEX.test(number))
      ) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: region === RegionCode.PT ? '7-14' : region === RegionCode.BR ? '11' : '9',
        letters: region === RegionCode.ES ? '1' : undefined,
        region: region,
      };
    } else {
      if (IDENTIFICATION_GENERIC_NUMBER_REGEX.test(number)) {
        return {valid: true};
      }
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_number,
        digits: '',
        region: undefined,
      };
    }
  }
  return {valid: true};
};

export const roomValidation = (
  value?: string | null
): {valid: boolean; error?: string; max?: number} => {
  if (value && value.trim().length > 0 && value.trim().length <= ROOM_NAME_MAX_CHARACTERS) {
    if (ROOM_NAME_REGEX.test(value)) {
      return {valid: true};
    } else {
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_room_name,
      };
    }
  }

  if (value && value.trim().length > ROOM_NAME_MAX_CHARACTERS) {
    return {
      valid: false,
      error: translations.user_form.errors.form_validation_room_maximum_characters,
      max: ROOM_NAME_MAX_CHARACTERS,
    };
  }

  return {
    valid: true,
  };
};

export const quickAccessValidation = (
  code?: string
): {valid: boolean; error?: string; max?: number} => {
  // Validate Quick Access Code
  if (code && code !== '****') {
    if (QUICK_ACCESS_CODE_REGEX.test(code)) {
      return {
        valid: true,
      };
    } else {
      return {
        valid: false,
        error: translations.user_form.errors.form_validation_invalid_quick_access_code,
        max: QUICK_ACCESS_CODE_MAX_CHARACTERS,
      };
    }
  }

  return {
    valid: true,
  };
};
